package com.zi.support.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement

internal data class WebAccessCode(
    val id: String,
    val accessCode: String,
    val companyName: String,
    val serverUrl: String,
    val environment: String,
    val dataServer: String,
    val active: Boolean,
    val live: Boolean,
    val test: Boolean,
    val updatedAt: Double,
    /** The server's SSL certificate status from manageAccessCode, e.g. "SSL valid · 45 days left"; tone good | warn | bad. */
    val certificateLabel: String = "",
    val certificateTone: String = ""
)

internal data class AccessCodeTestState(
    val checking: Boolean = false,
    val tested: Boolean = false,
    val ok: Boolean = false,
    val latencyMs: Int = 0,
    val error: String = ""
)

/**
 * "Access codes" — More > Access codes. The Driver Hub code → company server directory
 * (operations_access_codes). Every read and write goes through the manageAccessCode Cloud
 * Function, which validates records and runs the server health test.
 */
internal object AccessCodesScreen {
    private var codes: List<WebAccessCode> = emptyList()
    private val testStates: MutableMap<String, AccessCodeTestState> = mutableMapOf()
    private var search: String = ""
    private var filter: String = "all"
    private var isTestingAll: Boolean = false

    fun render() {
        element("content")?.innerHTML = """
          $MORE_BACK
          <div class="page-intro access-codes-intro">
            <div>
              <h2>Access codes</h2>
              <p>The five-digit code a driver types into Driver Hub to reach their company server.</p>
            </div>
            <div class="page-intro-actions">
              <button id="access-test-all" class="btn btn-ghost" title="Check connection status of all access code servers">
                <span class="material-symbols-rounded">speed</span>
                <span id="access-test-all-label">Test all</span>
              </button>
              <button id="access-add" class="btn btn-primary">
                <span class="material-symbols-rounded">add</span>
                Add access code
              </button>
            </div>
          </div>
          <div class="access-toolbar">
            <div class="search-shell access-search-shell">
              <span class="material-symbols-rounded" aria-hidden="true">search</span>
              <label class="visually-hidden" for="access-search">Search access codes</label>
              <input id="access-search" class="input search" placeholder="Search code, company or server…">
            </div>
            <div class="segmented access-filters" id="access-filters" role="tablist" aria-label="Filter access codes">
              <button data-filter="all" class="active" role="tab">All</button>
              <button data-filter="active" role="tab">Active</button>
              <button data-filter="inactive" role="tab">Inactive</button>
              <button data-filter="live" role="tab">Live</button>
              <button data-filter="test" role="tab">Test</button>
            </div>
            <span id="access-summary" class="access-summary"></span>
          </div>
          <div id="access-list-root">
            <div class="empty">Loading access codes…</div>
          </div>
        """.trimIndent()

        bindMoreBack()
        click("access-add") { openEditor(null) }
        click("access-test-all") { testAll() }

        bindSearchClear("access-search") {
            search = ""
            paint()
        }
        element("access-search")?.addEventListener("input", {
            search = input("access-search").trim().lowercase()
            paint()
        })

        each("[data-filter]") { button ->
            button.addEventListener("click", {
                filter = button.getAttribute("data-filter") ?: "all"
                each("[data-filter]") { it.classList.remove("active") }
                button.classList.add("active")
                paint()
            })
        }

        load()
    }

    private fun load() {
        val root = element("access-list-root") ?: return
        root.innerHTML = "<div class=\"empty\">Loading access codes…</div>"
        promise(FirebaseGateway.callable("manageAccessCode", json("action" to "list")), { result ->
            val list = result.data.codes as? Array<dynamic> ?: emptyArray()
            codes = list.map { item ->
                WebAccessCode(
                    id = field(item, "id"),
                    accessCode = field(item, "accessCode"),
                    companyName = field(item, "companyName"),
                    serverUrl = field(item, "serverUrl"),
                    environment = field(item, "environment").ifBlank { "Production" },
                    dataServer = field(item, "dataServer"),
                    active = item.active as? Boolean ?: true,
                    live = item.live as? Boolean ?: false,
                    test = item.test as? Boolean ?: false,
                    updatedAt = (item.updatedAt as? Number)?.toDouble() ?: 0.0,
                    certificateLabel = field(item, "certificateLabel"),
                    certificateTone = field(item, "certificateTone")
                )
            }
            paint()
        }, { message ->
            root.innerHTML = "<div class=\"empty\">${escape(message)}</div>"
        })
    }

    private fun paint() {
        paintStats()
        paintList()
    }

    private fun paintStats() {
        val tested = testStates.values.filter { it.tested }
        val active = codes.count { it.active }
        element("access-summary")?.textContent = "${codes.size} codes · $active active" +
            if (tested.isEmpty()) "" else " · ${tested.count { it.ok }}/${tested.size} online"
    }

    private fun filterCodes(): List<WebAccessCode> {
        return codes.filter { code ->
            val matchesFilter = when (filter) {
                "active" -> code.active
                "inactive" -> !code.active
                "live" -> code.live
                "test" -> code.test
                else -> true
            }
            val matchesSearch = search.isBlank() ||
                code.accessCode.lowercase().contains(search) ||
                code.companyName.lowercase().contains(search) ||
                code.serverUrl.lowercase().contains(search) ||
                code.environment.lowercase().contains(search) ||
                code.dataServer.lowercase().contains(search)

            matchesFilter && matchesSearch
        }
    }

    private fun paintList() {
        val root = element("access-list-root") ?: return
        val filtered = filterCodes()

        if (codes.isEmpty()) {
            root.innerHTML = """
              <div class="empty">
                <span class="material-symbols-rounded" style="font-size:44px;color:var(--muted);margin-bottom:8px">pin</span>
                <h3>No access codes yet</h3>
                <p>Add your first five-digit Driver Hub access code to connect drivers to company servers.</p>
                <button id="access-empty-add" class="btn btn-primary" style="margin-top:14px">
                  <span class="material-symbols-rounded">add</span> Add access code
                </button>
              </div>
            """.trimIndent()
            click("access-empty-add") { openEditor(null) }
            return
        }

        if (filtered.isEmpty()) {
            root.innerHTML = """
              <div class="empty">
                <span class="material-symbols-rounded" style="font-size:38px;color:var(--muted);margin-bottom:6px">search_off</span>
                <h3>No matching access codes</h3>
                <p>Try clearing your search query or switching your active filter.</p>
              </div>
            """.trimIndent()
            return
        }

        root.innerHTML = """
          <div class="access-code-table-wrap">
            <table class="access-code-table">
              <thead><tr><th>Code</th><th>Company</th><th>Server</th><th>Status</th><th></th></tr></thead>
              <tbody>${filtered.joinToString("") { renderTableRowHtml(it, codes.indexOf(it)) }}</tbody>
            </table>
          </div>
        """.trimIndent()

        filtered.forEach { code ->
            val index = codes.indexOf(code)
            // Copy code
            click("ac-copy-code-$index") {
                copyText(code.accessCode, "Copied code ${code.accessCode} to clipboard")
            }
            // Copy URL
            click("ac-copy-url-$index") {
                copyText(code.serverUrl, "Copied server URL to clipboard")
            }
            // Test
            click("access-test-$index") {
                testSingle(code, index)
            }
            // Edit
            click("access-edit-$index") {
                openEditor(code)
            }
            // Live servers: clicking the row (not its buttons) shows certificate and connection details
            if (code.live) element("ac-row-$index")?.addEventListener("click", { event ->
                if (event.target.asDynamic().closest("button") == null) openDetails(code)
            })
        }
    }

    private fun renderTableRowHtml(code: WebAccessCode, index: Int): String {
        val testState = testStates[code.id] ?: AccessCodeTestState()
        val statusInfo = computeStatusBadge(testState)
        return """
          <tr id="ac-row-$index" class="${if (!code.active) "is-inactive" else ""}${if (code.live) " is-live" else ""}"${if (code.live) " title=\"Show server details\"" else ""}>
            <td><button class="ac-code" id="ac-copy-code-$index" title="Copy code">${escape(code.accessCode)}</button></td>
            <td><strong>${escape(code.companyName)}</strong><small class="ac-sub">${escape(listOf(code.environment, code.dataServer).filter(String::isNotBlank).joinToString(" · "))}</small></td>
            <td><button class="ac-url" id="ac-copy-url-$index" title="Copy ${escape(code.serverUrl)}">${escape(code.serverUrl)}</button></td>
            <td>
              <div class="ac-status-stack">
                <span class="ac-badge ${if (code.active) "ac-badge-active" else "ac-badge-inactive"}">${if (code.active) "Active" else "Inactive"}</span>
                <div class="ac-status-badge ${statusInfo.containerClass}">
                  <span class="api-dot ${statusInfo.dotClass}"></span>
                  <span class="ac-status-text" id="access-status-$index">${escape(statusInfo.label)}</span>
                </div>
                ${if (code.certificateLabel.isBlank()) "" else "<span class=\"ac-cert ac-cert-${escape(code.certificateTone)}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">${if (code.certificateTone == "good") "verified_user" else "gpp_maybe"}</span>${escape(code.certificateLabel)}</span>"}
              </div>
            </td>
            <td class="ac-actions">
              <button class="btn btn-sm btn-ghost" id="access-test-$index" ${if (testState.checking) "disabled" else ""}>Test</button>
              <button class="btn btn-sm btn-icon" id="access-edit-$index" title="Edit" aria-label="Edit ${escape(code.companyName)}"><span class="material-symbols-rounded">edit</span></button>
            </td>
          </tr>
        """.trimIndent()
    }

    private data class BadgeInfo(val containerClass: String, val dotClass: String, val label: String)

    private fun computeStatusBadge(state: AccessCodeTestState): BadgeInfo {
        return when {
            state.checking -> BadgeInfo("checking", "", "Testing connection…")
            !state.tested -> BadgeInfo("", "", "Ready to test")
            state.ok -> {
                val dotClass = if (state.latencyMs > 2000) "warn" else "good"
                BadgeInfo("online", dotClass, "Online · ${state.latencyMs}ms")
            }
            else -> BadgeInfo("offline", "bad", "Offline · ${state.error.ifBlank { "No response" }}")
        }
    }

    private fun testSingle(code: WebAccessCode, index: Int, onFinished: (() -> Unit)? = null) {
        testStates[code.id] = AccessCodeTestState(checking = true)
        updateStatusElement(index, computeStatusBadge(testStates[code.id]!!))

        val button = element("access-test-$index") as? HTMLButtonElement
        button?.disabled = true

        promise(FirebaseGateway.callable("manageAccessCode", json("action" to "check", "id" to code.id)), { result ->
            button?.disabled = false
            val data = result.data
            val ok = data.ok as? Boolean ?: false
            val latencyMs = (data.latencyMs as? Number)?.toInt() ?: 0
            val error = field(data, "error")

            testStates[code.id] = AccessCodeTestState(
                checking = false,
                tested = true,
                ok = ok,
                latencyMs = latencyMs,
                error = error
            )
            updateStatusElement(index, computeStatusBadge(testStates[code.id]!!))
            paintStats()
            onFinished?.invoke()
        }, { message ->
            button?.disabled = false
            testStates[code.id] = AccessCodeTestState(
                checking = false,
                tested = true,
                ok = false,
                error = message
            )
            updateStatusElement(index, computeStatusBadge(testStates[code.id]!!))
            paintStats()
            onFinished?.invoke()
        })
    }

    private fun updateStatusElement(index: Int, info: BadgeInfo) {
        val labelNode = element("access-status-$index") ?: return
        labelNode.textContent = info.label
        val badge = labelNode.parentElement as? HTMLElement ?: return
        badge.className = "ac-status-badge ${info.containerClass}"
        val dot = badge.querySelector(".api-dot") as? HTMLElement
        dot?.className = "api-dot ${info.dotClass}"
    }

    private fun testAll() {
        if (isTestingAll || codes.isEmpty()) return
        isTestingAll = true
        val label = element("access-test-all-label")
        val button = element("access-test-all") as? HTMLButtonElement
        button?.disabled = true

        var completed = 0
        val total = codes.size

        fun checkNext(currentIndex: Int) {
            if (currentIndex >= codes.size) {
                isTestingAll = false
                button?.disabled = false
                label?.textContent = "Test all"
                val online = testStates.values.count { it.tested && it.ok }
                val offline = testStates.values.count { it.tested && !it.ok }
                toast("Server health check completed: $online online, $offline down.")
                return
            }

            label?.textContent = "Testing ${currentIndex + 1}/$total…"
            val code = codes[currentIndex]
            testSingle(code, currentIndex) {
                completed++
                checkNext(currentIndex + 1)
            }
        }

        checkNext(0)
    }

    private fun openEditor(code: WebAccessCode?) {
        val editing = code != null

        fun initial(extractor: (WebAccessCode) -> String) = if (editing) escape(extractor(code!!)) else ""

        val initialCode = initial { it.accessCode }
        val initialCompany = initial { it.companyName }
        val initialUrl = initial { it.serverUrl }
        val initialEnv = if (editing) escape(code!!.environment) else "Production"
        val initialData = initial { it.dataServer }
        val initialActive = if (editing) code!!.active else true

        modal(if (editing) "Edit access code" else "Add access code", """
          <div class="form-grid">
            <div class="field">
              <label for="ac-code">Access code (5 digits) *</label>
              <input id="ac-code" class="input ac-input-code" inputmode="numeric" maxlength="5" pattern="[0-9]{5}" placeholder="12345" value="$initialCode" autocomplete="off" required>
              <small class="field-help">Drivers type this five-digit code into Driver Hub.</small>
            </div>
            <div class="field">
              <label for="ac-company">Company name *</label>
              <input id="ac-company" class="input" maxlength="100" placeholder="e.g. Gantrans" value="$initialCompany" required>
            </div>
            <div class="field span-2">
              <label for="ac-url">Server URL *</label>
              <input id="ac-url" class="input" placeholder="https://company.net.za:56308" value="$initialUrl" required>
              <small class="field-help">Must be https://host:port with no path (e.g. https://gantrans.net.za:56308).</small>
            </div>
            <div class="field">
              <label for="ac-env">Environment</label>
              <input id="ac-env" class="input" list="ac-env-options" placeholder="Production" value="$initialEnv">
              <datalist id="ac-env-options">
                <option value="Production">
                <option value="Private network">
                <option value="Demo">
              </datalist>
            </div>
            <div class="field">
              <label for="ac-data">Data server tag (optional)</label>
              <input id="ac-data" class="input" placeholder="e.g. live_gt" value="$initialData">
            </div>
            <label class="choice-card span-2">
              <input id="ac-active" type="checkbox" ${if (initialActive) "checked" else ""}>
              <span>
                <strong>Active in Driver Hub</strong><br>
                <small>When unchecked, the code is retained for records but drivers cannot use it to connect.</small>
              </span>
            </label>
          </div>
          <div id="ac-error" style="color:var(--danger);font-size:13px;margin-top:12px;font-weight:600"></div>
        """.trimIndent(),
            (if (editing) "<button id=\"ac-delete\" class=\"btn btn-danger\" style=\"margin-right:auto\">Delete</button>" else "") +
                "<button id=\"ac-cancel\" class=\"btn\">Cancel</button><button id=\"ac-save\" class=\"btn btn-primary\">Save</button>",
            wide = false
        )

        // Restrict code input strictly to digits
        element("ac-code")?.addEventListener("input", {
            val inputEl = element("ac-code") as? HTMLInputElement
            if (inputEl != null) {
                inputEl.value = inputEl.value.filter { it.isDigit() }.take(5)
            }
        })

        click("ac-cancel") { closeModal() }

        click("ac-save") {
            val rawCode = input("ac-code")
            val company = input("ac-company")
            val url = input("ac-url")
            val errorBox = element("ac-error")

            if (!Regex("^\\d{5}$").matches(rawCode)) {
                errorBox?.textContent = "The access code must be exactly 5 numeric digits."
                return@click
            }
            if (company.isBlank()) {
                errorBox?.textContent = "Please enter a company name."
                return@click
            }
            if (url.isBlank() || !url.startsWith("https://")) {
                errorBox?.textContent = "Server URL must start with https:// (e.g. https://company.net.za:56308)."
                return@click
            }

            errorBox?.textContent = ""
            val saveBtn = element("ac-save") as? HTMLButtonElement
            saveBtn?.disabled = true
            saveBtn?.textContent = "Saving…"

            val payload = json(
                "action" to "save",
                "id" to if (editing) code?.id else null,
                "accessCode" to rawCode,
                "companyName" to company,
                "serverUrl" to url,
                "environment" to input("ac-env").ifBlank { "Production" },
                "dataServer" to input("ac-data"),
                "active" to checked("ac-active")
            )

            promise(FirebaseGateway.callable("manageAccessCode", payload), {
                closeModal()
                toast("Access code $rawCode saved successfully")
                load()
            }, { message ->
                saveBtn?.disabled = false
                saveBtn?.textContent = "Save"
                errorBox?.textContent = message
            })
        }

        if (editing) {
            click("ac-delete") {
                val button = element("ac-delete") as? HTMLElement ?: return@click
                if (button.getAttribute("data-armed") != "1") {
                    button.setAttribute("data-armed", "1")
                    button.textContent = "Tap again to delete ${code!!.accessCode}"
                    return@click
                }
                button.asDynamic().disabled = true
                promise(FirebaseGateway.callable("manageAccessCode", json("action" to "delete", "id" to code!!.id)), {
                    closeModal()
                    toast("Access code ${code.accessCode} deleted")
                    load()
                }, { message ->
                    button.asDynamic().disabled = false
                    element("ac-error")?.textContent = message
                })
            }
        }
    }

    private fun openDetails(code: WebAccessCode) {
        modal("${code.companyName} · ${code.accessCode}", """
          <p class="ac-details-url">${escape(code.serverUrl)}</p>
          <div id="ac-details" class="ac-details"><div class="empty">Checking server and certificate…</div></div>
        """.trimIndent(), "<button id=\"ac-details-edit\" class=\"btn\">Edit</button><button id=\"ac-details-refresh\" class=\"btn btn-primary\">Check again</button>")
        click("ac-details-edit") { openEditor(code) }
        fun loadDetails() {
            val root = element("ac-details") ?: return
            root.innerHTML = "<div class=\"empty\">Checking server and certificate…</div>"
            promise(FirebaseGateway.callable("manageAccessCode", json("action" to "details", "id" to code.id)), { result ->
                val rows = result.data.rows as? Array<dynamic> ?: emptyArray()
                root.innerHTML = "<dl>" + rows.joinToString("") { row ->
                    "<dt>${escape(field(row, "label"))}</dt><dd class=\"${escape(field(row, "tone"))}\">${escape(field(row, "value"))}</dd>"
                } + "</dl>"
            }, { message -> root.innerHTML = "<div class=\"empty\">${escape(message)}</div>" })
        }
        click("ac-details-refresh") { loadDetails() }
        loadDetails()
    }

    private fun copyText(text: String, label: String) {
        if (text.isBlank()) return
        val nav = window.navigator.asDynamic()
        if (nav != null && nav.clipboard != null && nav.clipboard.writeText != null) {
            nav.clipboard.writeText(text).then({
                toast(label)
            }, {
                fallbackCopy(text, label)
            })
        } else {
            fallbackCopy(text, label)
        }
    }

    private fun fallbackCopy(text: String, label: String) {
        val ta = document.createElement("textarea") as HTMLTextAreaElement
        ta.value = text
        ta.style.position = "fixed"
        ta.style.opacity = "0"
        document.body?.appendChild(ta)
        ta.select()
        try {
            val copied = js("document.execCommand('copy')") as? Boolean ?: true
            if (copied) toast(label) else toast("Could not copy: $text", true)
        } catch (_: Throwable) {
            toast("Could not copy: $text", true)
        } finally {
            ta.remove()
        }
    }

    private fun each(selector: String, action: (HTMLElement) -> Unit) {
        val nodes = document.querySelectorAll(selector)
        for (i in 0 until nodes.length) {
            (nodes.item(i) as? HTMLElement)?.let(action)
        }
    }
}
